if (!customElements.get('product-complementary-products')) {
    customElements.define(
      'product-complementary-products',
      class ProductComplementaryProducts extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('.Product_custom_complementary_products_button');
          this.buttonTextNodes = [...(this.button?.querySelectorAll('.Product_custom_complementary_products_button_text') || [])];
          this.buttonTextNodes.forEach((buttonText) => {
            buttonText.dataset.defaultText = buttonText.textContent?.trim() || '';
          });
          this.section = this.closest('.shopify-section');

          this.addEventListener('change', this.handleSelectionChange);
          this.section?.addEventListener('change', this.handleMainProductChange);
          this.section?.addEventListener('input', this.handleMainProductChange);
          this.button?.addEventListener('click', this.handleAddSelected);
          this.updateButtonState();
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.handleSelectionChange);
          this.section?.removeEventListener('change', this.handleMainProductChange);
          this.section?.removeEventListener('input', this.handleMainProductChange);
          this.button?.removeEventListener('click', this.handleAddSelected);
        }

        handleSelectionChange = (event) => {
          this.syncSelectedVariant(event);
          this.updateButtonState();
        };

        handleMainProductChange = (event) => {
          const target = event?.target;

          if (!(target instanceof HTMLInputElement)) return;
          if (!target.closest('product-form-component')) return;
          if (target.name !== 'id' && target.name !== 'quantity') return;

          this.updateButtonState();
        };

        syncSelectedVariant(event) {
          const selectedOption = event?.target;

          if (!(selectedOption instanceof HTMLInputElement) || !selectedOption.dataset.variantId) return;

          const item = selectedOption.closest('.Product_custom_complementary_products_item');
          const checkbox = item?.querySelector('.Product_custom_complementary_products_checkbox');

          if (!(checkbox instanceof HTMLInputElement)) return;

          checkbox.value = selectedOption.dataset.variantId;
          const selectedVariant = this.getVariantPriceData(item, selectedOption.dataset.variantId);

          if (selectedVariant) {
            checkbox.dataset.price = selectedVariant.price;
            checkbox.dataset.compareAtPrice = selectedVariant.compareAtPrice || selectedVariant.price;
            checkbox.disabled = selectedVariant.available === false;
            this.updateItemImage(item, selectedVariant);
          } else {
            checkbox.disabled = selectedOption.dataset.optionAvailable === 'false';
            this.resetItemImage(item);
          }

          if (checkbox.disabled) {
            checkbox.checked = false;
          }
        }

        getVariantPriceData(item, variantId) {
          const priceScript = item?.querySelector('[data-complementary-variant-prices]');
          if (!priceScript?.textContent) return null;

          try {
            const variants = JSON.parse(priceScript.textContent);
            return variants.find((variant) => String(variant.id) === String(variantId)) || null;
          } catch (error) {
            return null;
          }
        }

        getImageElements(item) {
          const imageLink = item?.querySelector('.Product_custom_complementary_products_image_link');
          const image = imageLink?.querySelector('img');

          return {
            imageLink: imageLink instanceof HTMLAnchorElement ? imageLink : null,
            image: image instanceof HTMLImageElement ? image : null,
          };
        }

        getTextLink(item) {
          const textLink = item?.querySelector('.Product_title a');

          return textLink instanceof HTMLAnchorElement ? textLink : null;
        }

        updateItemImage(item, variantData) {
          const { imageLink, image } = this.getImageElements(item);
          const textLink = this.getTextLink(item);
          if (!imageLink || !image) return;

          if (variantData.url) {
            imageLink.href = variantData.url;
            if (textLink) textLink.href = variantData.url;
          }

          if (!variantData.image) {
            this.resetItemImage(item, true);
            return;
          }

          image.src = variantData.image.src || image.dataset.defaultSrc || image.src;
          image.alt = variantData.image.alt || image.dataset.defaultAlt || image.alt;

          if (variantData.image.width) {
            image.width = Number(variantData.image.width) || image.width;
            image.setAttribute('width', String(variantData.image.width));
          }

          if (variantData.image.height) {
            image.height = Number(variantData.image.height) || image.height;
            image.setAttribute('height', String(variantData.image.height));
          }
        }

        resetItemImage(item, preserveHref = false) {
          const { imageLink, image } = this.getImageElements(item);
          const textLink = this.getTextLink(item);
          if (!imageLink || !image) return;

          if (!preserveHref) {
            const defaultHref = imageLink.dataset.defaultHref;
            if (defaultHref) {
              imageLink.href = defaultHref;
            }

            if (textLink?.dataset.defaultHref) {
              textLink.href = textLink.dataset.defaultHref;
            }
          }

          if (image.dataset.defaultSrc) {
            image.src = image.dataset.defaultSrc;
          }

          if (image.dataset.defaultAlt) {
            image.alt = image.dataset.defaultAlt;
          }

          if (image.dataset.defaultWidth) {
            image.width = Number(image.dataset.defaultWidth) || image.width;
            image.setAttribute('width', image.dataset.defaultWidth);
          }

          if (image.dataset.defaultHeight) {
            image.height = Number(image.dataset.defaultHeight) || image.height;
            image.setAttribute('height', image.dataset.defaultHeight);
          }
        }

        getMainProductSelection() {
          const shouldAddMainProduct = this.dataset.addMainProduct === 'true';
          if (!shouldAddMainProduct) return null;

          const mainProductInput = this.section?.querySelector('product-form-component input[name="id"]');
          if (!mainProductInput?.value) return null;

          const priceScript = this.querySelector('[data-main-product-variants]');
          if (!priceScript?.textContent) return null;

          try {
            const variants = JSON.parse(priceScript.textContent);
            const variant = variants.find((item) => String(item.id) === String(mainProductInput.value));

            if (!variant) return null;

            const quantityInput = this.section?.querySelector('product-form-component input[name="quantity"]');

            return {
              id: Number(mainProductInput.value),
              quantity: Number(quantityInput?.value) || 1,
              price: Number(variant.price) || 0,
              compareAtPrice: Number(variant.compareAtPrice) || Number(variant.price) || 0,
              available: variant.available !== false,
            };
          } catch (error) {
            return null;
          }
        }

        handleAddSelected = async () => {
          const selectedInputs = [...this.querySelectorAll('.Product_custom_complementary_products_checkbox:checked:not(:disabled)')];

          if (!selectedInputs.length || !this.button) return;

          const items = [];
          const mainProductSelection = this.getMainProductSelection();

          if (mainProductSelection?.id && mainProductSelection.available) {
            items.push({
              id: mainProductSelection.id,
              quantity: mainProductSelection.quantity,
            });
          }

          selectedInputs.forEach((input) => {
            items.push({
              id: Number(input.value),
              quantity: Number(input.dataset.quantity) || 1,
            });
          });

          this.button.disabled = true;
          this.button.setAttribute('aria-busy', 'true');
          this.buttonTextNodes.forEach((buttonText) => {
            buttonText.textContent = this.dataset.addingText || 'Adding...';
          });

          try {
            const sectionIds = [...document.querySelectorAll('cart-items-component')]
              .map((component) => component.dataset.sectionId)
              .filter(Boolean)
              .join(',');
            const payload = { items };

            if (sectionIds) payload.sections = sectionIds;

            const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify(payload),
            });
            const data = await response.json();

            if (!response.ok || data.status) {
              throw new Error(data.description || data.message || this.dataset.errorText || 'Unable to add selected products.');
            }

            const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            const cart = await cartResponse.json();

            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: this.id,
                  data: {
                    source: 'custom-complementary-products',
                    itemCount: cart.item_count,
                    sections: data.sections || {},
                  },
                },
              })
            );

            selectedInputs.forEach((input) => {
              input.checked = false;
            });
          } catch (error) {
            console.error(error);
          } finally {
            this.button.removeAttribute('aria-busy');
            this.updateButtonState();
          }
        };

        updateButtonState() {
          if (!this.button) return;
          const selectedInputs = [...this.querySelectorAll('.Product_custom_complementary_products_checkbox:checked:not(:disabled)')];
          const hasSelection = selectedInputs.length > 0;
          const isBusy = this.button.getAttribute('aria-busy') === 'true';
          this.button.disabled = !hasSelection || isBusy;

          if (!isBusy) {
            this.updateButtonText(selectedInputs);
          }
        }

        updateButtonText(selectedInputs) {
          if (!this.buttonTextNodes?.length) return;

          if (!selectedInputs.length) {
            this.buttonTextNodes.forEach((buttonText) => {
              buttonText.textContent = buttonText.dataset.defaultText || '';
            });
            return;
          }

          const totals = selectedInputs.reduce(
            (sum, input) => {
              const quantity = Number(input.dataset.quantity) || 1;
              sum.price += (Number(input.dataset.price) || 0) * quantity;
              sum.compareAtPrice += (Number(input.dataset.compareAtPrice) || Number(input.dataset.price) || 0) * quantity;
              return sum;
            },
            { price: 0, compareAtPrice: 0 }
          );

          const mainProductSelection = this.getMainProductSelection();
          if (mainProductSelection?.available) {
            totals.price += mainProductSelection.price * mainProductSelection.quantity;
            totals.compareAtPrice += mainProductSelection.compareAtPrice * mainProductSelection.quantity;
          }

          const price = this.formatMoney(totals.price);
          const compareAtPrice = totals.compareAtPrice > totals.price ? ` ${this.formatMoney(totals.compareAtPrice)}` : '';
          this.buttonTextNodes.forEach((buttonText) => {
            buttonText.textContent = `${buttonText.dataset.defaultText || ''} – ${price}${compareAtPrice}`;
          });
        }

        formatMoney(cents) {
          const amount = (Number(cents) || 0) / 100;
          const formattedAmount = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);

          return (this.dataset.currencySymbol || '$') + formattedAmount;
        }

      }
    );
  }
